// Skygge-runde (Forslag E) — auto-scroll.
//
// Tekst skubbes fra højre mod venstre med fast hastighed (`cps`
// = characters per second). En stationær cursor i midten af
// vinduet markerer hvor brugeren skal være. Hvis brugerens
// faktiske cursor falder bagud i forhold til den forventede
// cursor, vises en mild advarsel og hastigheden falder lidt.
//
// Pædagogisk pointe: tvinger jævnt tempo og fjerner muligheden
// for at "tænke længe". Kun anbefalet til niveau 3+ challenge-
// runder hvor de fleste taster sidder fast.

import { useEffect, useMemo, useState } from 'react';
import type {
  KeyboardLayoutId,
  RoundPresentation,
  RoundResult,
  Trin,
} from '../../types';
import type { PreparedRound } from '../../lib/lessonGenerator';
import { useRoundInput } from '../../lib/useRoundInput';
import Keyboard from '../Keyboard';

interface ShadowRoundProps {
  round: PreparedRound;
  trin: Trin;
  keyboardLayout: KeyboardLayoutId;
  strictCase?: boolean;
  allowAltLayer?: boolean;
  keyboardLayoutId?: KeyboardLayoutId;
  onFinish: (r: RoundResult) => void;
}

/** Char width estimate (in rem). Used to translate text horizontally. */
const CHAR_REM = 1.2;
const VISIBLE_CHARS = 24;
const CURSOR_OFFSET = Math.floor(VISIBLE_CHARS / 2);
const BEHIND_WARNING_CHARS = 3;
const BEHIND_SLOWDOWN_CHARS = 5;
const SLOWDOWN_FACTOR = 0.85;

export default function ShadowRound({
  round,
  trin,
  keyboardLayout,
  strictCase = false,
  allowAltLayer = false,
  keyboardLayoutId,
  onFinish,
}: ShadowRoundProps) {
  const targetText = round.targetText;
  const mode = round.template.mode;
  const presentation = round.template.presentation as Extract<
    RoundPresentation,
    { kind: 'shadow' }
  >;
  const baseCps = Math.max(1, Math.min(10, presentation?.cps ?? 4));

  const input = useRoundInput({
    targetText,
    mode,
    roundId: round.template.id,
    roundTitle: round.template.title,
    presentationKind: 'shadow',
    strictCase,
    allowAltLayer,
    keyboardLayoutId: keyboardLayoutId ?? keyboardLayout,
    onFinish,
  });

  const [cps, setCps] = useState(baseCps);
  const [scrollStartedAt, setScrollStartedAt] = useState<number | null>(null);
  const [now, setNow] = useState(Date.now());

  useEffect(() => {
    if (scrollStartedAt === null && input.elapsedMs > 0) {
      setScrollStartedAt(Date.now());
    }
  }, [input.elapsedMs, scrollStartedAt]);

  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), 80);
    return () => window.clearInterval(id);
  }, []);

  const expectedCursor = useMemo(() => {
    if (scrollStartedAt === null) return 0;
    const elapsed = (now - scrollStartedAt) / 1000;
    return Math.max(0, Math.floor(elapsed * cps));
  }, [now, cps, scrollStartedAt]);

  const behindBy = Math.max(0, expectedCursor - input.cursor);
  const tooFarBehind = behindBy >= BEHIND_WARNING_CHARS;

  useEffect(() => {
    if (behindBy >= BEHIND_SLOWDOWN_CHARS && cps > baseCps * 0.6) {
      setCps((c) => Math.max(baseCps * 0.6, c * SLOWDOWN_FACTOR));
    }
  }, [behindBy, baseCps, cps]);

  // paddingLeft already centers char 0 under the cursor — shift by anchor only.
  const anchor = Math.max(expectedCursor, input.cursor);
  const shiftChars = Math.max(0, anchor);

  return (
    <>
      <div className="grid grid-cols-4 gap-3">
        <Stat label="Tid" value={formatMs(input.elapsedMs)} />
        <Stat label="WPM" value={input.liveWpm.toFixed(0)} />
        <Stat label="Tempo" value={`${cps.toFixed(1)} t/s`} />
        <Stat
          label="Tegn"
          value={`${input.cursor} / ${targetText.length}`}
        />
      </div>

      <div className="surface relative overflow-hidden p-6">
        <div
          className="relative mx-auto"
          style={{
            width: `${VISIBLE_CHARS * CHAR_REM}rem`,
            maxWidth: '100%',
          }}
        >
          <div
            className="pointer-events-none absolute top-0 bottom-0 z-10"
            style={{
              left: `${CURSOR_OFFSET * CHAR_REM}rem`,
              width: `${CHAR_REM}rem`,
              background:
                'linear-gradient(180deg, rgba(99,102,241,0.10), rgba(99,102,241,0.25), rgba(99,102,241,0.10))',
              borderLeft: '2px solid rgb(99,102,241)',
            }}
            aria-hidden
          />
          <div
            className="shadow-track text-strong"
            style={{
              paddingLeft: `${CURSOR_OFFSET * CHAR_REM}rem`,
              transform: `translateX(${-shiftChars * CHAR_REM}rem)`,
              transition: 'transform 80ms linear',
            }}
          >
            {[...targetText].map((ch, i) => {
              const isTyped = i < input.cursor;
              const isExpected = i === input.cursor;
              const cls = isTyped
                ? 'text-emerald-500 dark:text-emerald-300'
                : isExpected
                  ? 'text-zinc-900 dark:text-zinc-100'
                  : 'text-zinc-400 dark:text-zinc-500';
              return (
                <span key={i} className={cls} style={{ width: `${CHAR_REM}rem`, textAlign: 'center' }}>
                  {ch === ' ' ? '\u2423' : ch}
                </span>
              );
            })}
            <span style={{ width: `${VISIBLE_CHARS * CHAR_REM}rem` }} />
          </div>
        </div>

        <div className="mt-4 flex h-5 items-center justify-center">
          {tooFarBehind && (
            <p className="text-xs font-semibold text-amber-600 dark:text-amber-300 animate-fade-in-up">
              Du falder bagud — fokusér på den næste tast
            </p>
          )}
        </div>
      </div>

      <Keyboard
        layoutId={keyboardLayout}
        nextExpectedKey={input.expectedChar}
        lastPressedKey={input.lastPress?.key}
        lastPressCorrect={input.lastPress?.correct}
        unlockedKeys={trin.unlockedKeys}
        hintLevel="none"
        showFingerDot={false}
      />

      <p className="text-center text-xs text-faint">
        Teksten begynder at rulle, når du skriver første tast. Følg med. Tryk
        Escape for at afslutte runden tidligt.
      </p>
    </>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="surface-soft px-4 py-3">
      <p className="label">{label}</p>
      <p className="mt-1 font-mono text-2xl text-strong">{value}</p>
    </div>
  );
}

function formatMs(ms: number): string {
  const total = Math.ceil(ms / 1000);
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}
